/**
 * お気に入り管理サービス
 * LocalStorageを使用してお気に入り銘柄のスナップショットを永続化
 */

export interface FavoriteSnapshot {
  symbol: string;
  company_name: string;
  detection_date: string; // ISO 8601
  detection_result: {
    symbol: string;
    company_name: string;
    detection_date: string;
    change_point_date: string;
    confidence_score: number;
    algorithm_id: string;
    algorithm_name: string;
    parameters: Record<string, any>;
    detection_reason?: string;
    metrics?: any;
    required_days?: number;  // 検出に必要な日数（株価取得用）
  };
  company_info?: {
    company_overview: string;
    strengths: string[];
    weaknesses: string[];
    growth_prospects: string;
    recent_topics: Array<{
      title: string;
      summary: string;
      source_url: string;
      pub_date?: string;
    }>;
    last_updated: string;
  };
  added_at: string; // お気に入り登録日時
  updated_at: string; // 最終更新日時
}

const STORAGE_KEY = 'favorites_snapshots';
const MAX_FAVORITES = 100; // LocalStorage容量制限対策

/**
 * LocalStorageからお気に入り一覧を読み込む
 */
function loadFavorites(): FavoriteSnapshot[] {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    if (!data) {
      return [];
    }
    return JSON.parse(data);
  } catch (error) {
    console.error('❌ お気に入りデータの読み込みに失敗:', error);
    throw new Error(`お気に入りデータが破損しています: ${error}`);
  }
}

/**
 * LocalStorageにお気に入り一覧を保存
 */
function saveFavorites(favorites: FavoriteSnapshot[]): void {
  try {
    const data = JSON.stringify(favorites);
    localStorage.setItem(STORAGE_KEY, data);
  } catch (error) {
    console.error('❌ お気に入りデータの保存に失敗:', error);
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      throw new Error('お気に入りの保存容量が上限に達しました。古いお気に入りを削除してください。');
    }
    throw new Error(`お気に入りの保存に失敗しました: ${error}`);
  }
}

/**
 * お気に入りに登録
 */
export function addFavorite(snapshot: FavoriteSnapshot): void {
  const favorites = loadFavorites();

  // 同じ銘柄が既に存在する場合は更新
  const existingIndex = favorites.findIndex(f => f.symbol === snapshot.symbol);
  if (existingIndex !== -1) {
    // 既存のスナップショットを更新（company_infoは保持）
    favorites[existingIndex] = {
      ...snapshot,
      company_info: favorites[existingIndex].company_info || snapshot.company_info,
      added_at: favorites[existingIndex].added_at, // 登録日時は保持
    };
  } else {
    // 新規登録
    if (favorites.length >= MAX_FAVORITES) {
      throw new Error(`お気に入りは最大${MAX_FAVORITES}件までです。古いお気に入りを削除してください。`);
    }
    favorites.push(snapshot);
  }

  saveFavorites(favorites);
}

/**
 * お気に入りから削除
 */
export function removeFavorite(symbol: string): void {
  const favorites = loadFavorites();
  const filtered = favorites.filter(f => f.symbol !== symbol);

  if (filtered.length === favorites.length) {
    console.warn(`⚠️ ${symbol} はお気に入りに登録されていません`);
    return;
  }

  saveFavorites(filtered);
}

/**
 * お気に入りに登録されているか確認
 */
export function isFavorite(symbol: string): boolean {
  const favorites = loadFavorites();
  return favorites.some(f => f.symbol === symbol);
}

/**
 * お気に入り一覧を取得
 */
export function getAllFavorites(): FavoriteSnapshot[] {
  return loadFavorites();
}

/**
 * 特定銘柄のスナップショットを取得
 */
export function getFavorite(symbol: string): FavoriteSnapshot | null {
  const favorites = loadFavorites();
  return favorites.find(f => f.symbol === symbol) || null;
}

/**
 * スナップショットを部分更新
 * 主にOpenAI情報の追加に使用
 */
export function updateSnapshot(symbol: string, updates: Partial<FavoriteSnapshot>): void {
  const favorites = loadFavorites();
  const index = favorites.findIndex(f => f.symbol === symbol);

  if (index === -1) {
    throw new Error(`${symbol} はお気に入りに登録されていません`);
  }

  // 既存スナップショットに更新をマージ
  favorites[index] = {
    ...favorites[index],
    ...updates,
    symbol: favorites[index].symbol, // symbolは変更不可
    added_at: favorites[index].added_at, // 登録日時は変更不可
    updated_at: new Date().toISOString(), // 更新日時は常に現在時刻
  };

  saveFavorites(favorites);
}

/**
 * 再検出時のスナップショット更新
 * detection_result のみ更新し、company_info は保持
 */
export function updateOnRedetection(
  symbol: string,
  detectionResult: FavoriteSnapshot['detection_result']
): void {
  const favorites = loadFavorites();
  const index = favorites.findIndex(f => f.symbol === symbol);

  if (index === -1) {
    // お気に入り未登録なら何もしない
    return;
  }

  // 検出結果のみ更新、company_infoは保持
  favorites[index] = {
    ...favorites[index],
    detection_result: detectionResult,
    detection_date: detectionResult.detection_date,
    updated_at: new Date().toISOString(),
    // company_info, added_at は保持
  };

  saveFavorites(favorites);
}

/**
 * OpenAI情報取得時のスナップショット更新
 */
export function updateCompanyInfo(
  symbol: string,
  companyInfo: FavoriteSnapshot['company_info']
): void {
  updateSnapshot(symbol, { company_info: companyInfo });
}
